import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Security as SecurityIcon,
  Print as PrintIcon,
  Warning as WarningIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Add as AddIcon
} from '@mui/icons-material';

/**
 * Componente de Medicamentos Controlados - Tercera pestaña
 * Bitácora regulatoria y gestión de sustancias controladas
 */
const ControlledMedications = () => {
  const [loading, setLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState('farmacia-central');
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [showCountDialog, setShowCountDialog] = useState(false);
  const [reportPeriod, setReportPeriod] = useState('monthly');
  const [countForm, setCountForm] = useState({
    productId: '',
    actualCount: '',
    notes: '',
    witnessId: '',
    authorizedBy: ''
  });

  const locations = [
    { id: 'farmacia-central', name: 'Farmacia Central' },
    { id: 'pabellon-1', name: 'Pabellón 1' },
    { id: 'pabellon-2', name: 'Pabellón 2' },
    { id: 'carrito-anestesia-1', name: 'Carrito Anestesia 1' }
  ];

  // Datos de ejemplo - en producción vendrían de Firestore
  const stats = {
    totalControlled: 45,
    currentStock: 234,
    monthlyDispensations: 89,
    pendingCounts: 3
  };

  const controlledProducts = [
    {
      id: 1,
      name: 'Morfina 10mg/ml',
      genericName: 'Morfina',
      presentation: 'Ampolla 10mg/ml',
      totalStock: 45,
      minStockLevel: 10,
      locations: ['farmacia-central', 'pabellon-1'],
      nextExpiring: '2025-02-15'
    },
    {
      id: 2,
      name: 'Fentanilo 50mcg/ml',
      genericName: 'Fentanilo',
      presentation: 'Ampolla 50mcg/ml',
      totalStock: 23,
      minStockLevel: 5,
      locations: ['farmacia-central', 'carrito-anestesia-1'],
      nextExpiring: '2025-03-20'
    },
    {
      id: 3,
      name: 'Ketamina 100mg/ml',
      genericName: 'Ketamina',
      presentation: 'Vial 100mg/ml',
      totalStock: 67,
      minStockLevel: 15,
      locations: ['farmacia-central', 'pabellon-2'],
      nextExpiring: '2025-01-30'
    }
  ];

  const recentActivities = [
    {
      id: 1,
      product: 'Morfina 10mg/ml',
      action: 'Dispensado',
      quantity: 2,
      patient: 'Firulais - 12345',
      doctor: 'Dr. Pérez',
      nurse: 'Enf. González',
      timestamp: new Date('2024-12-28T10:30:00'),
      location: 'Pabellón 1'
    },
    {
      id: 2,
      product: 'Fentanilo 50mcg/ml',
      action: 'Contado',
      quantity: 50,
      patient: '-',
      doctor: 'Dr. López',
      nurse: 'Enf. Martínez',
      timestamp: new Date('2024-12-28T09:15:00'),
      location: 'Farmacia Central'
    },
    {
      id: 3,
      product: 'Ketamina 100mg/ml',
      action: 'Dispensado',
      quantity: 1,
      patient: 'Michifuz - 67890',
      doctor: 'Dr. Rodríguez',
      nurse: 'Enf. Silva',
      timestamp: new Date('2024-12-28T08:45:00'),
      location: 'Hospitalización'
    }
  ];

  const handleGenerateReport = () => {
    console.log('Generando reporte para:', reportPeriod);
    setShowReportDialog(false);
  };

  const handlePhysicalCount = () => {
    console.log('Realizando conteo físico:', countForm);
    setShowCountDialog(false);
    setCountForm({ productId: '', actualCount: '', notes: '', witnessId: '', authorizedBy: '' });
  };

  const getActivityColor = (action) => {
    switch (action) {
      case 'Dispensado': return 'primary';
      case 'Contado': return 'success';
      case 'Desperdiciado': return 'error';
      case 'Devuelto': return 'warning';
      default: return 'default';
    }
  };

  const getStockStatus = (product) => {
    if (product.totalStock === 0) return { label: 'Sin Stock', color: 'error' };
    if (product.totalStock <= product.minStockLevel) return { label: 'Stock Bajo', color: 'warning' };
    return { label: 'Disponible', color: 'success' };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          <SecurityIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
          Medicamentos Controlados
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Bitácora regulatoria y trazabilidad de medicamentos controlados
        </Typography>
      </Box>
      {/* Alerta de seguridad */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Importante:</strong> Esta sección maneja medicamentos controlados sujetos a regulación sanitaria. 
          Todas las transacciones requieren doble firma y quedan registradas en la bitácora electrónica.
        </Typography>
      </Alert>
      {/* Estadísticas */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h3" fontWeight="bold" color="primary">
              {stats.totalControlled}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Medicamentos Controlados
            </Typography>
          </Paper>
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h3" fontWeight="bold" color="success">
              {stats.currentStock}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Unidades en Stock
            </Typography>
          </Paper>
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h3" fontWeight="bold" color="info">
              {stats.monthlyDispensations}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Dispensaciones Mensuales
            </Typography>
          </Paper>
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h3" fontWeight="bold" color="warning">
              {stats.pendingCounts}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Conteos Pendientes
            </Typography>
          </Paper>
        </Grid>
      </Grid>
      {/* Grid principal */}
      <Grid container spacing={3}>
        {/* Lista de medicamentos controlados */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Inventario de Medicamentos Controlados
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {controlledProducts.map((product) => {
              const stockStatus = getStockStatus(product);
              
              return (
                <Paper key={product.id} sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {product.name}
                        </Typography>
                        <Chip
                          label="Controlado"
                          size="small"
                          color="error"
                        />
                        <Chip
                          label={stockStatus.label}
                          size="small"
                          color={stockStatus.color}
                        />
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary">
                        {product.genericName} - {product.presentation}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary">
                        Stock: {product.totalStock} unidades | Mínimo: {product.minStockLevel}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary">
                        Próximo vencimiento: {product.nextExpiring}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
                        {product.locations.map((loc) => (
                          <Chip
                            key={loc}
                            label={locations.find(l => l.id === loc)?.name || loc}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Ver detalles">
                        <IconButton size="small">
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Realizar conteo">
                        <IconButton size="small">
                          <CheckCircleIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        </Grid>

        {/* Actividad reciente */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              Actividad Reciente
            </Typography>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
              >
                {locations.map((location) => (
                  <MenuItem key={location.id} value={location.id}>
                    {location.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {recentActivities.map((activity) => (
              <Paper key={activity.id} sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {activity.product}
                      </Typography>
                      <Chip
                        label={activity.action}
                        color={getActivityColor(activity.action)}
                        size="small"
                      />
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary">
                      Cantidad: {activity.quantity} | Paciente: {activity.patient}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary">
                      Dr. {activity.doctor} | Enf. {activity.nurse}
                    </Typography>
                    
                    <Typography variant="caption" color="text.secondary">
                      {activity.timestamp.toLocaleString()} - {activity.location}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        </Grid>

        {/* Acciones regulatorias */}
        <Grid size={12}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Acciones Regulatorias
          </Typography>
          
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 3
              }}>
              <Button
                variant="contained"
                startIcon={<CheckCircleIcon />}
                onClick={() => setShowCountDialog(true)}
                fullWidth
              >
                Realizar Conteo Físico
              </Button>
            </Grid>
            
            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 3
              }}>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => setShowReportDialog(true)}
                fullWidth
              >
                Generar Reporte Regulatorio
              </Button>
            </Grid>
            
            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 3
              }}>
              <Button
                variant="outlined"
                startIcon={<VisibilityIcon />}
                fullWidth
              >
                Ver Bitácora Completa
              </Button>
            </Grid>
            
            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 3
              }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                fullWidth
              >
                Nuevo Medicamento Controlado
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {/* Diálogo de conteo físico */}
      <Dialog open={showCountDialog} onClose={() => setShowCountDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Conteo Físico de Medicamentos Controlados</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Este conteo debe ser realizado por dos personas: un contador y un testigo.
          </Alert>
          
          <Grid container spacing={2}>
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>Medicamento</InputLabel>
                <Select
                  value={countForm.productId}
                  onChange={(e) => setCountForm({...countForm, productId: e.target.value})}
                >
                  {controlledProducts.map((product) => (
                    <MenuItem key={product.id} value={product.id}>
                      {product.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={12}>
              <TextField
                fullWidth
                label="Cantidad Real Contada"
                type="number"
                value={countForm.actualCount}
                onChange={(e) => setCountForm({...countForm, actualCount: e.target.value})}
              />
            </Grid>
            
            <Grid size={12}>
              <TextField
                fullWidth
                label="Notas del Conteo"
                multiline
                rows={3}
                value={countForm.notes}
                onChange={(e) => setCountForm({...countForm, notes: e.target.value})}
              />
            </Grid>
            
            <Grid size={6}>
              <TextField
                fullWidth
                label="Autorizado por (ID)"
                value={countForm.authorizedBy}
                onChange={(e) => setCountForm({...countForm, authorizedBy: e.target.value})}
              />
            </Grid>
            
            <Grid size={6}>
              <TextField
                fullWidth
                label="Testigo (ID)"
                value={countForm.witnessId}
                onChange={(e) => setCountForm({...countForm, witnessId: e.target.value})}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCountDialog(false)}>Cancelar</Button>
          <Button onClick={handlePhysicalCount} variant="contained">
            Registrar Conteo
          </Button>
        </DialogActions>
      </Dialog>
      {/* Diálogo de reportes */}
      <Dialog open={showReportDialog} onClose={() => setShowReportDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generar Reporte Regulatorio</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Período del Reporte</InputLabel>
            <Select
              value={reportPeriod}
              onChange={(e) => setReportPeriod(e.target.value)}
            >
              <MenuItem value="daily">Diario</MenuItem>
              <MenuItem value="weekly">Semanal</MenuItem>
              <MenuItem value="monthly">Mensual</MenuItem>
              <MenuItem value="quarterly">Trimestral</MenuItem>
              <MenuItem value="yearly">Anual</MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            fullWidth
            label="Notas adicionales"
            multiline
            rows={3}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowReportDialog(false)}>Cancelar</Button>
          <Button onClick={handleGenerateReport} variant="contained">
            Generar Reporte
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ControlledMedications;
